import React from 'react';
import {
  ApiOutlined,
  ClockCircleOutlined,
  DisconnectOutlined,
  FieldNumberOutlined,
  LockOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  StopOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { orbitType } from '../../styles';
import { blockPeriodApplies, formatSupportDateTime } from '../supportFormat';
import { SupportBlockMeta, SupportGovernedState } from '../types';
import { noop, sp } from './supportStyles';

const T = `${TOrbit}.support.states`;

/** FR §4.24: these states stand in for the block's data; the rest annotate it. */
const REPLACES_DATA: ReadonlySet<SupportGovernedState> = new Set<SupportGovernedState>([
  'permission_restricted',
  'not_configured',
  'insufficient_data',
  'not_available',
  'upstream_error',
  'entitlement_unavailable'
]);

const STATE_LOOK: Partial<
  Record<SupportGovernedState, { Icon: React.ComponentType; color: string }>
> = {
  permission_restricted: { Icon: LockOutlined, color: sp.warning },
  not_configured: { Icon: SettingOutlined, color: sp.muted },
  insufficient_data: { Icon: QuestionCircleOutlined, color: sp.muted },
  not_available: { Icon: StopOutlined, color: sp.muted },
  upstream_error: { Icon: DisconnectOutlined, color: sp.danger },
  entitlement_unavailable: { Icon: ApiOutlined, color: sp.warning },
  stale: { Icon: ClockCircleOutlined, color: sp.warning },
  valid_zero: { Icon: FieldNumberOutlined, color: sp.link }
};

const Card = styled.div<{ $color: string }>`
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px solid ${sp.border};
  border-left: 3px solid ${({ $color }) => $color};
  border-radius: 10px;
  background: ${sp.inset};

  > :first-child {
    flex: none;
    margin: 2px 10px 0 0;
    color: ${({ $color }) => $color};
  }
`;

const Title = styled.p`
  margin: 0;
  color: ${sp.text};
  ${orbitType.containerBody}
  font-weight: 700;
`;

const Body = styled.p`
  margin: 2px 0 0;
  color: ${sp.muted};
  ${orbitType.containerBody}
`;

const Refresh = styled.button`
  margin-top: 6px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${sp.link};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 700;
`;

const MetaLine = styled.p`
  margin: 0;
  color: ${sp.muted};
  ${orbitType.helping}
`;

const Stack = styled.div`
  > * + * {
    margin-top: 8px;
  }
`;

export const StateCard: React.FC<{ state: SupportGovernedState }> = ({ state }) => {
  const { t } = useMultiLanguageTranslation();
  const look = STATE_LOOK[state];
  if (look === undefined) {
    return null;
  }
  const { Icon, color } = look;
  return (
    <Card
      $color={color}
      role={state === 'upstream_error' ? 'alert' : 'note'}
      data-testid="orbit-support-state"
      data-state={state}
    >
      <Icon aria-hidden="true" />
      <div>
        <Title>{t(`${T}.${state}.title`)}</Title>
        <Body>{t(`${T}.${state}.body`)}</Body>
        {state === 'stale' ? (
          <Refresh type="button" onClick={noop}>
            {t(`${T}.refresh`)}
          </Refresh>
        ) : null}
      </div>
    </Card>
  );
};

/**
 * Wraps one block in its governed state (FR §4.24), scope line (§4.10) and freshness line (§4.25).
 * The UI renders what the backend says; it infers no permission or state of its own (§4.4).
 */
const GovernedState: React.FC<{
  meta: SupportBlockMeta & { type: string };
  children: React.ReactNode;
}> = ({ meta, children }) => {
  const { t } = useMultiLanguageTranslation();
  const state = meta.state ?? 'ok';
  const scope = meta.scope ? (
    <MetaLine data-testid="orbit-support-scope">{`${meta.scope.tenant} · ${meta.scope.text}`}</MetaLine>
  ) : null;
  const period =
    meta.period && blockPeriodApplies(meta) ? (
      <MetaLine data-testid="orbit-support-period">
        {t(`${T}.period`, { period: meta.period })}
      </MetaLine>
    ) : null;
  const asOf = meta.as_of ? (
    <MetaLine data-testid="orbit-support-as-of">
      {t(`${T}.asOf`, { date: formatSupportDateTime(meta.as_of) })}
    </MetaLine>
  ) : null;

  if (state === 'ok' || state === 'jira_pending') {
    return scope === null && period === null && asOf === null ? (
      <>{children}</>
    ) : (
      <Stack>
        {scope}
        {period}
        {children}
        {asOf}
      </Stack>
    );
  }

  return (
    <Stack>
      {scope}
      {period}
      <StateCard state={state} />
      {REPLACES_DATA.has(state) ? null : children}
      {asOf}
    </Stack>
  );
};

export default GovernedState;
